import { Coverage } from '@app/domain/model/Coverage';
import { LifeInsuredPerson } from '@app/domain/model/LifeInsuredPerson';
import { Policy } from '@app/domain/model/policy/Policy';
import { InsuredPersonBeneficiary } from '@app/domain/model/policy/InsuredPersonBeneficiary';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { useCallback, useEffect, useMemo, useState, ChangeEvent } from 'react';
import { useBeneficiaryActions } from './useBeneficiaryActions';
import { useBeneficiaryFormState } from '@app/app/self-service/(wizard)/change-beneficiary-death/utils/useBeneficiaryFormState';

interface UseDeathBeneficiaryManagementParams {
    selectedInsuredPerson: Nullable<LifeInsuredPerson>;
    policyData: Nullable<Policy>;
    isEstonianEshop?: boolean;
}

export function useDeathBeneficiaryManagement({
    selectedInsuredPerson,
    policyData,
    isEstonianEshop = false,
}: UseDeathBeneficiaryManagementParams) {
    const beneficiaries = useMemo<InsuredPersonBeneficiary[]>(() => {
        const foundInsuredPerson = policyData?.insuredPersons?.find(person => person.id === selectedInsuredPerson?.id);
        const relevantCovers = [Coverage.LifeInsurance, Coverage.LifeInsuranceD, Coverage.DeathByAccident];
        const foundCovers =
            foundInsuredPerson?.coverages?.filter(coverageItem => relevantCovers.includes(coverageItem.cover)) || [];
        const firstWithBeneficiaries = foundCovers.find(coverageItem => (coverageItem.beneficiaries?.length ?? 0) > 0);
        return firstWithBeneficiaries?.beneficiaries || [];
    }, [policyData?.insuredPersons, selectedInsuredPerson?.id]);

    const insuredPersonAge = useMemo(() => {
        return parseLithuanianPersonalCode(selectedInsuredPerson?.personalCode ?? '')?.age;
    }, [selectedInsuredPerson?.personalCode]);

    const isMinor = useMemo(() => (insuredPersonAge ?? 0) < 18, [insuredPersonAge]);

    const hasNextOfKin = useMemo(() => beneficiaries.some(beneficiary => beneficiary?.code == null), [beneficiaries]);

    const hasAnyNamedBeneficiary = useMemo(
        () => beneficiaries.some(beneficiary => beneficiary?.code != null),
        [beneficiaries],
    );

    const legalHeirsDefault = isEstonianEshop ? false : isMinor || (hasNextOfKin && !hasAnyNamedBeneficiary);
    const [isLegalHeirSelected, setIsLegalHeirSelected] = useState<boolean>(legalHeirsDefault);

    useEffect(() => {
        if (isEstonianEshop) {
            setIsLegalHeirSelected(false);
        } else {
            setIsLegalHeirSelected(isMinor || (hasNextOfKin && !hasAnyNamedBeneficiary));
        }
    }, [isMinor, hasNextOfKin, hasAnyNamedBeneficiary, isEstonianEshop]);

    const effectiveBeneficiaries = useMemo<InsuredPersonBeneficiary[]>(() => {
        if (isLegalHeirSelected) return [];

        return beneficiaries.filter(beneficiary => beneficiary?.code != null);
    }, [beneficiaries, isLegalHeirSelected]);

    const beneficiaryFormState = useBeneficiaryFormState([], effectiveBeneficiaries, isEstonianEshop);

    const totalExistingShare = useMemo(() => {
        return beneficiaryFormState.existingBeneficiaries.reduce(
            (total: number, beneficiary) => total + (beneficiary.share || 0),
            0,
        );
    }, [beneficiaryFormState.existingBeneficiaries]);

    const beneficiaryActions = useBeneficiaryActions({
        beneficiaryFormState,
        totalExistingShare,
    });

    const combinedTotalShare = totalExistingShare + beneficiaryFormState.validation.totalShare;
    const isCombinedShareFull = useMemo(() => Math.abs(combinedTotalShare - 1) < 1e-6, [combinedTotalShare]);
    const canAddMoreBeneficiaries = useMemo(() => combinedTotalShare <= 1 - 0.1 + 1e-6, [combinedTotalShare]);

    const handleLegalHeirChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (isEstonianEshop) {
                return;
            }

            setIsLegalHeirSelected(event.target.checked);
        },
        [isEstonianEshop],
    );

    const getBeneficiariesPayload = useCallback(() => {
        if (isLegalHeirSelected) return [];

        return beneficiaryFormState.beneficiaries.map(beneficiary => ({
            id: beneficiary.id,
            name: beneficiary.name,
            firstName: beneficiary.firstName,
            lastName: beneficiary.lastName,
            personalCode: beneficiary.personalCode,
            share: beneficiary.share,
            address: beneficiary.address,
            email: beneficiary.email,
            phone: beneficiary.phone,
            isNew: beneficiary.isNew,
        }));
    }, [beneficiaryFormState.beneficiaries, isLegalHeirSelected]);

    return {
        beneficiaries,
        effectiveBeneficiaries,

        isMinor,
        hasNextOfKin,
        insuredPersonAge,

        legalHeirsDefault,
        isLegalHeirSelected,
        handleLegalHeirChange,

        beneficiaryFormState,
        existingBeneficiaries: beneficiaryFormState.existingBeneficiaries,
        newBeneficiaries: beneficiaryFormState.newBeneficiaries,

        totalExistingShare,
        combinedTotalShare,
        isCombinedShareFull,
        canAddMoreBeneficiaries,

        isEditingBeneficiary: beneficiaryActions.isEditingBeneficiary,
        editingBeneficiaryId: beneficiaryActions.editingBeneficiaryId,
        saveAttempted: beneficiaryActions.saveAttempted,
        editingBeneficiary: beneficiaryActions.editingBeneficiary,
        handleAddBeneficiary: beneficiaryActions.handleAddBeneficiary,
        handleEditBeneficiary: beneficiaryActions.handleEditBeneficiary,
        handleCancelBeneficiaryEdit: beneficiaryActions.handleCancelBeneficiaryEdit,
        handleSaveBeneficiary: beneficiaryActions.handleSaveBeneficiary,
        handleSetEditingBeneficiary: beneficiaryActions.handleSetIsEditingBeneficiary,

        getBeneficiariesPayload,
    };
}
